import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, MessageCircle, User, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LiveChat Component
 * Real-time chat feed for live game coverage
 * Uses the existing comments API with a dedicated article_id for live chat
 */
const LiveChat = ({ chatId = 'live-game-chat', title = 'Live Chat' }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (err) {
        // User not logged in
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/comments/${chatId}`);
      // Sort by created_at ascending for chat (oldest first)
      const sortedMessages = (response.data || []).sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      setMessages(sortedMessages);
      setError('');
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  // Initial fetch and polling
  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Auto-scroll to bottom when new messages arrive (within chat container only)
  useEffect(() => {
    if (messagesEndRef.current && messagesEndRef.current.parentElement) {
      // Scroll within the container, not the page
      const container = messagesEndRef.current.parentElement;
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    setSending(true);
    setError('');

    try {
      await axios.post(
        `${BACKEND_URL}/api/comments`,
        {
          article_id: chatId,
          content: newMessage.trim()
        },
        { withCredentials: true }
      );
      setNewMessage('');
      // Immediately fetch new messages
      await fetchMessages();
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error sending message:', err);
      if (err.response?.status === 403) {
        setError('Please set a nickname in your profile to chat');
      } else {
        setError('Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  // Format timestamp
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className="flex flex-col h-full bg-[#071a3d] rounded-lg overflow-hidden" data-testid="live-chat">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0b4e9b] to-[#8e0e2c] px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-white" />
          <span className="text-white text-xs font-bold uppercase tracking-wide">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-green-400 text-[10px] font-semibold animate-pulse">● {messages.length} msgs</span>
          <button 
            onClick={fetchMessages}
            className="text-white/70 hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px] max-h-[350px] bg-[#0a1628]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
            <MessageCircle className="w-8 h-8 mb-2 opacity-50" />
            <p>No messages yet</p>
            <p className="text-xs">Be the first to chat!</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div 
              key={msg.comment_id || idx} 
              className="flex gap-2 animate-fadeIn"
              data-testid={`chat-message-${idx}`}
            >
              {/* Avatar */}
              <div className="flex-shrink-0">
                {msg.user_picture ? (
                  <img 
                    src={msg.user_picture} 
                    alt={msg.user_name}
                    className="w-7 h-7 rounded-full object-cover border border-blue-500/30"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              
              {/* Message Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold text-blue-400 truncate">
                    {msg.user_name || 'Fan'}
                  </span>
                  <span className="text-[10px] text-gray-500">
                    {formatTime(msg.created_at)}
                  </span>
                </div>
                <p className="text-sm text-gray-200 break-words leading-snug">
                  {msg.content}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-2 bg-[#0d1f3c] border-t border-gray-700/50">
        {!user ? (
          <a 
            href="/login"
            className="block text-center text-xs text-blue-400 hover:text-blue-300 py-2"
            data-testid="chat-login-prompt"
          >
            Sign in to join the conversation
          </a>
        ) : (
          <form onSubmit={handleSend} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Say something..."
              maxLength={280}
              className="flex-1 bg-[#1a2d4d] text-white text-sm rounded-lg px-3 py-2 border border-gray-600/50 focus:border-blue-500 focus:outline-none placeholder-gray-500"
              disabled={sending}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-700 text-white px-3 py-2 rounded-lg transition-all"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        )}
        {error && (
          <p className="text-red-400 text-xs mt-1 text-center">{error}</p>
        )}
      </div>
    </div>
  );
};

export default LiveChat;
